/**
 * src/components/public/layout/ThemeToggle.tsx
 *
 * Light/Dark theme toggle butonu — animasyonlu (Magic UI tarzi).
 *
 * Tiklayinca lib/theme/animated-theme-change.ts helper'ini cagirir; View
 * Transitions API destekleyen tarayicilarda butonun konumundan acilan
 * clip-path circle animasyonu ile tema gecisi olur. Eski tarayicilar ve
 * prefers-reduced-motion -> instant switch.
 *
 * SSR hydration guard: mounted state olmadan ikon render edilmez (resolvedTheme
 * SSR'da undefined). Mount oncesi statik Sun ikonu placeholder gosterir.
 *
 * Client Component — useTheme + useState/useEffect + DOM API (ref).
 */

'use client'

import { Moon, Sun } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useTheme } from 'next-themes'
import { useEffect, useRef, useState } from 'react'

import { triggerAnimatedThemeChange } from '@/lib/theme/animated-theme-change'

/**
 * ThemeToggle — public header'da gosterilen tema gecisi butonu.
 *
 * Tiklayinca light <-> dark tema arasi gecis yapar; destekleyen tarayicilarda
 * View Transitions API ile clip-path circle expand animasyonu uygular.
 */
export function ThemeToggle() {
  const t = useTranslations('theme')
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Hydration uyumsuzlugunu onlemek icin mount sonrasi gercek ikon render.
  useEffect(() => {
    setMounted(true)
  }, [])

  const isDark = mounted && resolvedTheme === 'dark'

  const handleToggle = () => {
    const nextTheme = isDark ? 'light' : 'dark'
    triggerAnimatedThemeChange({
      origin: buttonRef.current,
      nextTheme,
      apply: () => setTheme(nextTheme),
    })
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleToggle}
      aria-label={t('toggle_label')}
      className="inline-flex h-11 w-11 items-center justify-center rounded-md text-brand-200/80 hover:text-white hover:bg-white/10 transition-colors"
    >
      {/* SSR'da statik bir icon goster — hydration sonrasi gercek tema'ya gore degisir */}
      {mounted ? (
        isDark ? (
          <Sun size={15} aria-hidden="true" />
        ) : (
          <Moon size={15} aria-hidden="true" />
        )
      ) : (
        <Sun size={15} aria-hidden="true" />
      )}
    </button>
  )
}
